/**
 * Reports API client (ADR-0060) — the tenant-global Report Type registry.
 *
 * A Report Type is the recursive typed DTO CONTRACT band layouts bind to
 * (ADR-0061): a tree of `{id,label,description,type,children}` nodes. `id`s
 * are the stable machine binding keys (labels are display-only). Physical
 * reports + providers ride in later slices.
 */
import { api } from "@/lib/api";

/** Scalar node types — mirror backend `hub.reports.schema.SCALAR_TYPES`. */
export const REPORT_SCALAR_TYPES = [
  "text",
  "integer",
  "decimal",
  "date",
  "datetime",
  "bool",
  "image",
] as const;

/** Container node types — `object` nests one record, `collection` repeats. */
export const REPORT_CONTAINER_TYPES = ["object", "collection"] as const;

export type ReportScalarType = (typeof REPORT_SCALAR_TYPES)[number];
export type ReportContainerType = (typeof REPORT_CONTAINER_TYPES)[number];
export type ReportNodeType = ReportScalarType | ReportContainerType;

export interface ReportTypeNode {
  id: string;
  label: string;
  description?: string;
  type: ReportNodeType;
  children?: ReportTypeNode[];
}

/** Data provider config (ADR-0060 S2) — how the Type's DTO gets filled.
 *  App-LESS by design: the `code` runtime reaches EVERY app through the
 *  mandatory `slug/tabella` namespace (no app binding on the Type). */
export interface ReportProvider {
  kind: "code" | "passthrough" | "dataset" | "sql";
  /** `code` kind — the transpiled JS body QuickJS runs. */
  code?: string | null;
  /** `code` kind — the TS source the editor round-trips. */
  code_ts?: string | null;
  /** `dataset` kind (S6) — the catalog Dataset id (each row = one instance). */
  dataset_id?: string | null;
  /** `dataset` kind — the visual QueryBuilder's SHAPE (ADR-0062 S4/C2):
   *  `columns` projects a subset (empty = all), `filters` is a flat AND list. */
  columns?: string[];
  filters?: ReportDatasetFilter[];
  /** `sql` kind (S6) — a DB credential + a parametrised read-only SELECT. */
  credential_id?: string | null;
  sql?: string | null;
  /** `sql` kind — the declared TYPED contract for the `:name` binds (ADR-0062 S1).
   *  Optional; an undeclared bind still works as a required string. */
  params?: ReportParam[];
}

/** A flat, ANDed row filter over a `dataset` provider (ADR-0062 S4/C2) — the
 *  `{column, op, value}` leaf the external `filtered_query` / internal
 *  `DataRowService` both accept. AND-only (OR isn't expressible over external). */
export interface ReportDatasetFilter {
  column: string;
  op: string;
  value?: unknown;
}

/** R2b/R2c — a param's OPTIONS source: the guided forms render a dropdown fed
 *  by it. `kind` picks the source (default `table`): an app table
 *  (`slug/tabella`), a catalog Dataset, a read-only SQL SELECT over a DB
 *  credential, or a fixed `choices` list. `value` = the option value
 *  (column/field/result column; sql defaults to the first column), `label` =
 *  the displayed text (absent ⇒ label = value; on table/dataset that means
 *  DISTINCT values of `value`). */
export interface ParamOptionsFrom {
  kind?: "table" | "dataset" | "sql" | "static";
  table?: string | null;
  dataset_id?: string | null;
  credential_id?: string | null;
  sql?: string | null;
  value?: string | null;
  label?: string | null;
  choices?: { value: string | number | boolean; label?: string | null }[] | null;
}

/** One declared, typed report param — the AppQuery `QueryParam` vocabulary
 *  (ADR-0062 S1). For a `sql` provider it's the `:name` binds; for a `code`
 *  provider it's the TS `interface Input` extracted at save (R2). */
export interface ReportParam {
  name: string;
  type: "string" | "number" | "boolean" | "object" | "array";
  required?: boolean;
  description?: string | null;
  /** R2b/R2c — dynamic options source: None = free input. */
  options_from?: ParamOptionsFrom | null;
}

export interface ReportType {
  id: string;
  name: string;
  description: string | null;
  schema: ReportTypeNode[];
  provider: ReportProvider | null;
  /** Param names the provider requires (sql binds) — drives the guided
   *  preview-params form in the designer. Derived server-side. */
  param_names: string[];
  /** The TYPED param contract (name/type/required/description) — drives the
   *  designer's typed preview form (ADR-0062 S1). Derived server-side. */
  param_schema: ReportParam[];
  created_at: string;
  updated_at: string;
}

export interface ReportTypeInput {
  name: string;
  description?: string | null;
  schema?: ReportTypeNode[];
  provider?: ReportProvider | null;
}

export interface ReportDataResult {
  rows: Record<string, unknown>[];
  count: number;
}

/** A physical report — one named band-model LAYOUT (ReportAst) over a Type
 *  (ADR-0061). The layout is an opaque JSON tree here; the S4 designer edits
 *  it visually, until then the page exposes a raw JSON editor. */
export interface ReportSummary {
  id: string;
  report_type_id: string;
  name: string;
  description: string | null;
  layout: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface ReportInput {
  name: string;
  description?: string | null;
  layout?: Record<string, unknown>;
}

/** Authenticated raw fetch for the render endpoint — the response is a
 *  DOCUMENT (text/html or application/pdf), not JSON, so it bypasses the
 *  api wrapper (mirrors `toolsApi.downloadOutput`). */
async function renderFetch(
  typeId: string,
  reportId: string,
  body: { format: "html" | "pdf"; params?: Record<string, unknown>; instances?: unknown[] },
): Promise<Response> {
  const { useAuthStore } = await import("./auth");
  const token = useAuthStore.getState().token;
  const res = await fetch(`/api/admin/report-types/${typeId}/reports/${reportId}/render`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const data = (await res.json()) as { detail?: unknown; error?: { message?: string } };
      detail = String(data.error?.message ?? data.detail ?? detail);
    } catch {
      /* non-JSON error body */
    }
    throw new Error(detail);
  }
  return res;
}

export const reportTypesApi = {
  list: () => api.get<ReportType[]>("/report-types"),
  get: (id: string) => api.get<ReportType>(`/report-types/${id}`),
  create: (body: ReportTypeInput) => api.post<ReportType>("/report-types", body),
  update: (id: string, body: Partial<ReportTypeInput>) =>
    api.patch<ReportType>(`/report-types/${id}`, body),
  remove: (id: string) => api.delete<void>(`/report-types/${id}`),
  /** Test-run: resolve the Type's data (provider run, or passthrough payload). */
  data: (id: string, body: { params?: Record<string, unknown>; instances?: unknown[] }) =>
    api.post<ReportDataResult>(`/report-types/${id}/data`, body),
  /** "Deduci schema" (S6): derive the Type node tree from a dataset/sql provider. */
  deduceSchema: (provider: ReportProvider) =>
    api.post<{ schema: ReportTypeNode[] }>("/report-types/deduce-schema", { provider }),

  // -- physical reports (ADR-0061 S3) --------------------------------------
  listReports: (typeId: string) => api.get<ReportSummary[]>(`/report-types/${typeId}/reports`),
  createReport: (typeId: string, body: ReportInput) =>
    api.post<ReportSummary>(`/report-types/${typeId}/reports`, body),
  updateReport: (typeId: string, reportId: string, body: Partial<ReportInput>) =>
    api.patch<ReportSummary>(`/report-types/${typeId}/reports/${reportId}`, body),
  removeReport: (typeId: string, reportId: string) =>
    api.delete<void>(`/report-types/${typeId}/reports/${reportId}`),
  /** Paginated HTML preview — the SAME document the PDF prints, plus the
   *  compiler's screen pagination shell (DevExpress-style page sheets). */
  renderHtml: async (
    typeId: string,
    reportId: string,
    body: { params?: Record<string, unknown>; instances?: unknown[] } = {},
  ): Promise<string> => {
    const res = await renderFetch(typeId, reportId, { ...body, format: "html" });
    return res.text();
  },
  /** PDF download (client-side blob save). */
  renderPdf: async (
    typeId: string,
    reportId: string,
    filename: string,
    body: { params?: Record<string, unknown>; instances?: unknown[] } = {},
  ): Promise<void> => {
    const res = await renderFetch(typeId, reportId, { ...body, format: "pdf" });
    const { triggerBlobDownload } = await import("./api-workflows");
    triggerBlobDownload(await res.blob(), `${filename}.pdf`);
  },
};
